import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ExternalLink, FileText, RefreshCw, SquarePen, X } from 'lucide-react';

const normalizeCalendarDraftDateKey = (draft) => {
    const candidates = [draft?.deadline_date, draft?.start_at, draft?.start_date];
    for (const candidate of candidates) {
        const value = String(candidate || '').trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    }
    return '';
};

const normalizeCalendarDateTime = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value.includes('T') ? value : value.replace(' ', 'T');
};

const parseCalendarDraftDate = (dateKey) => {
    if (!dateKey) return null;
    const parsed = new Date(`${dateKey}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCalendarDraftDate = (dateKey, options = { month: 'short', day: 'numeric' }) => {
    const parsed = parseCalendarDraftDate(dateKey);
    return parsed ? parsed.toLocaleDateString(undefined, options) : 'Date needed';
};

const getCalendarDraftMonthKey = (dateKey) => dateKey ? dateKey.slice(0, 7) : '';

const formatCalendarDraftMonth = (monthKey) => {
    const [year, month] = String(monthKey || '').split('-').map(Number);
    if (!year || !month) return 'Needs date';
    return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const stripCalendarDraftCasePrefix = (title = '', caseName = '') => {
    const value = String(title || '').trim() || 'Calendar draft';
    const prefix = String(caseName || '').trim();
    return prefix && value.startsWith(`${prefix} - `) ? value.slice(prefix.length + 3).trim() : value;
};

const formatCalendarDraftType = (eventType = '') => String(eventType || 'calendar_draft')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const normalizeSchedulingOrderCalendarDrafts = (answer) => {
    const caseName = String(answer?.case_name || '').trim();
    return (Array.isArray(answer?.calendar_drafts) ? answer.calendar_drafts : [])
        .map((draft, index) => {
            const dateKey = normalizeCalendarDraftDateKey(draft);
            return {
                ...draft,
                key: String(draft?.id || draft?.draft_id || `${dateKey || 'undated'}-${index}`),
                dateKey,
                monthKey: getCalendarDraftMonthKey(dateKey),
                displayTitle: stripCalendarDraftCasePrefix(draft?.title || draft?.calendar_title, caseName),
                displayType: formatCalendarDraftType(draft?.event_type),
                needsDate: !dateKey || draft?.metadata?.needs_user_date === true,
            };
        })
        .sort((left, right) => {
            if (left.needsDate !== right.needsDate) return left.needsDate ? 1 : -1;
            return String(left.dateKey || '').localeCompare(String(right.dateKey || '')) || left.displayTitle.localeCompare(right.displayTitle);
        });
};

const CalendarDraftMiniMonth = ({ monthKey, entries }) => {
    const [year, month] = String(monthKey || '').split('-').map(Number);
    if (!year || !month) return null;
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = new Date(year, month - 1, 1).getDay();
    const entryDates = new Set(entries.map(entry => entry.dateKey));
    const cells = [
        ...Array.from({ length: firstWeekday }, (_, index) => ({ key: `blank-${index}`, blank: true })),
        ...Array.from({ length: daysInMonth }, (_, index) => {
            const day = index + 1;
            const dateKey = `${monthKey}-${String(day).padStart(2, '0')}`;
            return { key: dateKey, day, active: entryDates.has(dateKey) };
        }),
    ];

    return (
        <div className="library-rag-calendar-mini-month" aria-label={`${formatCalendarDraftMonth(monthKey)} calendar draft dates`}>
            <div className="library-rag-calendar-mini-weekdays">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={`${monthKey}-weekday-${day}-${index}`}>{day}</span>)}
            </div>
            <div className="library-rag-calendar-mini-grid">
                {cells.map(cell => (
                    <span key={cell.key} className={`${cell.blank ? 'blank ' : ''}${cell.active ? 'active' : ''}`.trim()}>
                        {cell.blank ? '' : cell.day}
                    </span>
                ))}
            </div>
        </div>
    );
};

const CalendarDraftReviewCard = ({ draft }) => (
    <article className={`library-rag-calendar-draft-card${draft.needsDate ? ' needs-date' : ''}`}>
        <div className="library-rag-calendar-draft-date">
            <strong>{draft.needsDate ? '--' : formatCalendarDraftDate(draft.dateKey, { day: '2-digit' })}</strong>
            <span>{draft.needsDate ? 'Date' : formatCalendarDraftDate(draft.dateKey, { month: 'short' })}</span>
        </div>
        <div className="library-rag-calendar-draft-card-copy">
            <div className="library-rag-calendar-draft-card-title-row">
                <strong>{draft.displayTitle}</strong>
                <span>{draft.statusLabel || 'Draft'}</span>
            </div>
            <div className="library-rag-calendar-draft-card-meta">
                <span>{draft.displayType}</span>
                {draft.source_file_id ? <span><FileText size={13} /> Source file #{draft.source_file_id}</span> : null}
            </div>
            {draft.source_excerpt && <p>{draft.source_excerpt}</p>}
        </div>
    </article>
);

export const SchedulingOrderCalendarDraftReview = ({ answer, onOpenCalendarPanel }) => {
    const isPendingConfirmation = answer?.mode === 'scheduling_order_calendar_pending_confirmation';
    const drafts = normalizeSchedulingOrderCalendarDrafts(answer);
    drafts.forEach(draft => {
        draft.statusLabel = isPendingConfirmation ? 'Proposed' : 'Draft';
    });
    const datedDrafts = drafts.filter(draft => !draft.needsDate);
    const undatedDrafts = drafts.filter(draft => draft.needsDate);
    const monthKeys = Array.from(new Set(datedDrafts.map(draft => draft.monthKey).filter(Boolean)));
    const [selectedMonthKey, setSelectedMonthKey] = useState(() => monthKeys[0] || '');
    const activeMonthKey = monthKeys.includes(selectedMonthKey) ? selectedMonthKey : (monthKeys[0] || '');
    const activeMonthDrafts = activeMonthKey ? datedDrafts.filter(draft => draft.monthKey === activeMonthKey) : datedDrafts;
    const caseId = Number(answer?.case_id || 0);
    const caseName = String(answer?.case_name || '').trim() || 'the case';

    if (drafts.length === 0) return null;

    return (
        <div className="library-rag-calendar-draft-review">
            <div className="library-rag-calendar-draft-header">
                <div>
                    <span className="library-rag-calendar-draft-eyebrow">Calendar Drafts</span>
                    <h4>{drafts.length} {isPendingConfirmation ? 'draft candidate' : 'CaseDB-only draft'}{drafts.length === 1 ? '' : 's'} for {caseName}</h4>
                    <p>{isPendingConfirmation ? 'Review and confirm below. Nothing has been saved yet.' : 'No Outlook calendars were updated.'}</p>
                </div>
                {caseId > 0 && (
                    <div className="library-rag-calendar-draft-actions">
                        {onOpenCalendarPanel && (
                            <button type="button" className="library-rag-calendar-draft-open" onClick={onOpenCalendarPanel}>
                                <CalendarDays size={14} /> Open calendar
                            </button>
                        )}
                        <a className="library-rag-calendar-draft-open" href={`/case/${caseId}`}>
                            <ExternalLink size={14} /> Open case
                        </a>
                    </div>
                )}
            </div>
            <div className="library-rag-calendar-draft-layout">
                <aside className="library-rag-calendar-draft-months" aria-label="Calendar draft months">
                    <div className="library-rag-calendar-draft-month-tabs">
                        {monthKeys.map(monthKey => (
                            <button key={monthKey} type="button" className={monthKey === activeMonthKey ? 'active' : ''} onClick={() => setSelectedMonthKey(monthKey)}>
                                <CalendarDays size={14} />
                                <span>{formatCalendarDraftMonth(monthKey)}</span>
                                <strong>{datedDrafts.filter(draft => draft.monthKey === monthKey).length}</strong>
                            </button>
                        ))}
                    </div>
                    {activeMonthKey && <CalendarDraftMiniMonth monthKey={activeMonthKey} entries={activeMonthDrafts} />}
                </aside>
                <div className="library-rag-calendar-draft-list" aria-label="Calendar draft entries">
                    {activeMonthKey && (
                        <div className="library-rag-calendar-draft-list-heading">
                            <span>{formatCalendarDraftMonth(activeMonthKey)}</span>
                            <small>{activeMonthDrafts.length} draft{activeMonthDrafts.length === 1 ? '' : 's'}</small>
                        </div>
                    )}
                    {activeMonthDrafts.map(draft => <CalendarDraftReviewCard key={draft.key} draft={draft} />)}
                    {undatedDrafts.length > 0 && (
                        <div className="library-rag-calendar-draft-undated">
                            <div className="library-rag-calendar-draft-list-heading">
                                <span>Needs date</span>
                                <small>{undatedDrafts.length} draft{undatedDrafts.length === 1 ? '' : 's'}</small>
                            </div>
                            {undatedDrafts.map(draft => <CalendarDraftReviewCard key={draft.key} draft={draft} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const normalizeCalendarPanelUsers = (entry) => {
    if (Array.isArray(entry?.emails_used)) return entry.emails_used.filter(Boolean).map(String);
    if (typeof entry?.emails_used === 'string') return entry.emails_used.split(',').map(email => email.trim()).filter(Boolean);
    if (Array.isArray(entry?.suggested_users)) return entry.suggested_users.filter(Boolean).map(String);
    if (Array.isArray(entry?.users)) return entry.users.map(user => user?.email || user).filter(Boolean).map(String);
    return [];
};

const normalizeCalendarPanelReminders = (entry) => {
    const raw = entry?.reminders ?? entry?.suggested_reminders;
    const values = Array.isArray(raw) ? raw : String(raw || '').split(',');
    return values
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value >= 0);
};

const normalizeCalendarPanelEntry = (entry, index, caseName = '') => {
    const dateKey = normalizeCalendarDraftDateKey(entry);
    const draftId = Number(entry?.draft_id || entry?.id || 0) || null;
    const isDraft = Boolean(entry?.is_draft || entry?.source === 'case_calendar_draft' || draftId || String(entry?.status || '').toLowerCase() === 'draft');
    const eventUuid = String(entry?.event_uuid || (draftId ? `draft-${draftId}` : `calendar-panel-${index}`));
    const title = String(entry?.calendar_title || entry?.title || 'Calendar entry').trim();
    const sourceStatus = String(entry?.statusLabel || entry?.status || '').trim();
    return {
        ...entry,
        key: `${isDraft ? 'draft' : 'live'}-${eventUuid}-${draftId || index}`,
        event_uuid: eventUuid,
        draft_id: draftId,
        isDraft,
        dateKey,
        monthKey: getCalendarDraftMonthKey(dateKey),
        displayTitle: stripCalendarDraftCasePrefix(title, caseName),
        displayType: formatCalendarDraftType(entry?.event_type || (isDraft ? 'calendar_draft' : 'calendar_entry')),
        statusLabel: sourceStatus || (isDraft ? 'Draft' : 'Pushed'),
        needsDate: !dateKey || entry?.metadata?.needs_user_date === true,
        users: normalizeCalendarPanelUsers(entry),
        reminderValues: normalizeCalendarPanelReminders(entry),
    };
};

const normalizeCalendarPanelEntries = (payload, caseName = '') => {
    const liveEntries = (Array.isArray(payload?.deadlines) ? payload.deadlines : []).map((entry, index) => normalizeCalendarPanelEntry({ ...entry, is_draft: false, statusLabel: 'Pushed' }, index, caseName));
    const draftEntries = (Array.isArray(payload?.calendar_drafts) ? payload.calendar_drafts : []).map((entry, index) => normalizeCalendarPanelEntry({ ...entry, is_draft: true, statusLabel: 'Draft' }, index + liveEntries.length, caseName));
    return [...liveEntries, ...draftEntries].sort((left, right) => {
        if (left.needsDate !== right.needsDate) return left.needsDate ? 1 : -1;
        return String(left.dateKey || '').localeCompare(String(right.dateKey || '')) || left.displayTitle.localeCompare(right.displayTitle);
    });
};

const buildCalendarPanelEditDraft = (entry) => {
    const start = normalizeCalendarDateTime(entry?.start_date || entry?.start_at) || (entry?.dateKey ? `${entry.dateKey}T00:00:00` : '');
    const end = normalizeCalendarDateTime(entry?.end_date || entry?.end_at) || start;
    const isAllDay = Number(entry?.isAllDay ?? entry?.all_day ?? entry?.is_all_day ?? 1) === 1;
    const users = normalizeCalendarPanelUsers(entry);
    const reminders = normalizeCalendarPanelReminders(entry);
    return {
        event_uuid: entry?.event_uuid || '',
        draft_id: entry?.draft_id || null,
        isDraft: Boolean(entry?.isDraft),
        calendarStatus: entry?.isDraft ? 'draft' : 'pushed',
        subject: String(entry?.calendar_title || entry?.title || entry?.displayTitle || '').trim(),
        date: start ? start.split('T')[0] : '',
        time: start?.split('T')[1]?.slice(0, 5) || '',
        endDate: end ? end.split('T')[0] : (start ? start.split('T')[0] : ''),
        endTime: end?.split('T')[1]?.slice(0, 5) || '',
        isAllDay,
        users,
        usersText: users.join(', '),
        reminders,
        remindersText: reminders.join(', '),
        notes: String(entry?.notes || entry?.description || '').trim(),
    };
};

export const JaneCalendarPlannerPanel = ({ answer, onClose, apiBase }) => {
    const caseId = Number(answer?.case_id || 0);
    const caseName = String(answer?.case_name || '').trim() || (caseId > 0 ? `Case ${caseId}` : 'Calendar');
    const fallbackEntries = useMemo(() => normalizeSchedulingOrderCalendarDrafts(answer).map((entry, index) => normalizeCalendarPanelEntry({ ...entry, is_draft: true, statusLabel: answer?.mode === 'scheduling_order_calendar_pending_confirmation' ? 'Proposed' : 'Draft' }, index, caseName)), [answer, caseName]);
    const [entries, setEntries] = useState(fallbackEntries);
    const [loadingCalendar, setLoadingCalendar] = useState(false);
    const [calendarError, setCalendarError] = useState('');
    const [editDraft, setEditDraft] = useState(null);
    const [saveStatus, setSaveStatus] = useState('');
    const [savingEntry, setSavingEntry] = useState(false);

    const loadCalendar = async () => {
        if (!caseId) {
            setEntries(fallbackEntries);
            return;
        }
        setLoadingCalendar(true);
        setCalendarError('');
        try {
            const response = await fetch(`${apiBase}/deadlines.php?case_id=${caseId}`);
            const data = await response.json();
            if (!response.ok || !data?.success) throw new Error(data?.error || `Calendar load failed (${response.status})`);
            const nextEntries = normalizeCalendarPanelEntries(data, caseName);
            setEntries(nextEntries.length > 0 ? nextEntries : fallbackEntries);
        } catch (error) {
            setEntries(fallbackEntries);
            setCalendarError(error instanceof Error ? error.message : 'Could not load calendar entries.');
        } finally {
            setLoadingCalendar(false);
        }
    };

    useEffect(() => {
        setEntries(fallbackEntries);
        setEditDraft(null);
        setSaveStatus('');
        loadCalendar();
    }, [caseId]);

    const openEdit = (entry) => {
        setSaveStatus('');
        if (!entry?.draft_id && String(entry?.event_uuid || '').startsWith('calendar-panel-')) {
            setEditDraft(null);
            setSaveStatus('Create the drafts first, then edit the saved calendar entries here.');
            return;
        }
        setEditDraft(buildCalendarPanelEditDraft(entry));
    };

    const updateEditDraft = (key, value) => setEditDraft(prev => ({ ...(prev || {}), [key]: value }));

    const saveEditDraft = async (event) => {
        event.preventDefault();
        if (!editDraft || !caseId) return;
        const users = String(editDraft.usersText || '').split(',').map(value => value.trim()).filter(Boolean);
        const reminders = String(editDraft.remindersText || '').split(',').map(value => Number(value.trim())).filter(value => Number.isFinite(value) && value >= 0);
        if (!editDraft.subject.trim() || !editDraft.date) {
            setSaveStatus('Title and date are required.');
            return;
        }
        if (editDraft.calendarStatus === 'pushed' && users.length === 0) {
            setSaveStatus('Add at least one recipient before pushing to calendars.');
            return;
        }
        const startDateTime = editDraft.isAllDay ? `${editDraft.date}T00:00:00` : `${editDraft.date}T${editDraft.time || '00:00'}:00`;
        const endDateTime = editDraft.isAllDay ? startDateTime : `${editDraft.endDate || editDraft.date}T${editDraft.endTime || editDraft.time || '00:00'}:00`;
        setSavingEntry(true);
        setSaveStatus('Saving...');
        try {
            const response = await fetch(`${apiBase}/deadlines.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_uuid: editDraft.event_uuid,
                    draft_id: editDraft.draft_id,
                    calendar_status: editDraft.calendarStatus,
                    users,
                    subject: editDraft.subject,
                    start_date: startDateTime,
                    end_date: endDateTime,
                    notes: editDraft.notes,
                    isAllDay: editDraft.isAllDay,
                    reminders,
                    case_id: caseId,
                    files: [],
                }),
            });
            const data = await response.json();
            const graphError = Array.isArray(data?.results) ? data.results.find(result => result?.error)?.error : null;
            if (!response.ok || !data?.success || graphError) throw new Error(graphError?.message || data?.error || `Calendar save failed (${response.status})`);
            setSaveStatus(editDraft.calendarStatus === 'pushed' ? 'Saved and pushed to calendars.' : 'Saved as draft.');
            setEditDraft(null);
            await loadCalendar();
        } catch (error) {
            setSaveStatus(error instanceof Error ? error.message : 'Could not save calendar entry.');
        } finally {
            setSavingEntry(false);
        }
    };

    const monthKeys = Array.from(new Set(entries.map(entry => entry.monthKey).filter(Boolean)));
    const undatedCount = entries.filter(entry => entry.needsDate).length;

    return (
        <>
            <div className="library-rag-preview-header">
                <div className="library-rag-preview-heading">
                    <div className="library-rag-preview-title">Case Calendar</div>
                    <div className="library-rag-preview-meta">{caseName} · {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
                </div>
                <div className="library-rag-preview-actions" aria-label="Calendar panel actions">
                    <button type="button" className="library-rag-preview-control" onClick={loadCalendar} aria-label="Refresh calendar" title="Refresh calendar"><RefreshCw size={16} /></button>
                    <button type="button" className="library-rag-preview-control" onClick={onClose} aria-label="Close calendar" title="Close calendar"><X size={16} /></button>
                </div>
            </div>
            <div className="library-rag-calendar-panel">
                {monthKeys.length > 0 && (
                    <div className="library-rag-calendar-panel-months">
                        {monthKeys.map(monthKey => <span key={monthKey}>{formatCalendarDraftMonth(monthKey)} · {entries.filter(entry => entry.monthKey === monthKey).length}</span>)}
                        {undatedCount > 0 && <span>Needs date · {undatedCount}</span>}
                    </div>
                )}
                {calendarError && <div className="library-rag-calendar-panel-status warning">{calendarError}</div>}
                {loadingCalendar && <div className="library-rag-calendar-panel-status">Loading calendar...</div>}
                <div className="library-rag-calendar-panel-list">
                    {entries.map(entry => (
                        <article key={entry.key} className={`library-rag-calendar-panel-entry${entry.needsDate ? ' needs-date' : ''}`}>
                            <div className="library-rag-calendar-draft-date">
                                <strong>{entry.needsDate ? '--' : formatCalendarDraftDate(entry.dateKey, { day: '2-digit' })}</strong>
                                <span>{entry.needsDate ? 'Date' : formatCalendarDraftDate(entry.dateKey, { month: 'short' })}</span>
                            </div>
                            <div className="library-rag-calendar-panel-entry-copy">
                                <div className="library-rag-calendar-panel-entry-title"><strong>{entry.displayTitle}</strong><span>{entry.statusLabel}</span></div>
                                <div className="library-rag-calendar-panel-entry-meta">{entry.displayType}{entry.users.length > 0 ? ` · ${entry.users.length} recipient${entry.users.length === 1 ? '' : 's'}` : ''}{entry.reminderValues.length > 0 ? ` · ${entry.reminderValues.join('/')} day reminders` : ''}</div>
                                {entry.source_excerpt && <p>{entry.source_excerpt}</p>}
                            </div>
                            <button type="button" className="library-rag-calendar-panel-edit" onClick={() => openEdit(entry)}><SquarePen size={14} /> Edit</button>
                        </article>
                    ))}
                </div>
                {saveStatus && <div className="library-rag-calendar-panel-status">{saveStatus}</div>}
                {editDraft && (
                    <form className="library-rag-calendar-panel-editor" onSubmit={saveEditDraft}>
                        <div className="library-rag-calendar-panel-editor-topline">
                            <strong>Edit calendar entry</strong>
                            <button type="button" onClick={() => setEditDraft(null)} aria-label="Close editor"><X size={14} /></button>
                        </div>
                        <label>Title<input value={editDraft.subject} onChange={(event) => updateEditDraft('subject', event.target.value)} /></label>
                        <label>Status<select value={editDraft.calendarStatus} onChange={(event) => updateEditDraft('calendarStatus', event.target.value)}><option value="draft">Draft</option><option value="pushed">Pushed</option></select></label>
                        <div className="library-rag-calendar-panel-editor-grid">
                            <label>Start date<input type="date" value={editDraft.date} onChange={(event) => updateEditDraft('date', event.target.value)} /></label>
                            {!editDraft.isAllDay && <label>Start time<input type="time" value={editDraft.time} onChange={(event) => updateEditDraft('time', event.target.value)} /></label>}
                            {!editDraft.isAllDay && <label>End date<input type="date" value={editDraft.endDate} onChange={(event) => updateEditDraft('endDate', event.target.value)} /></label>}
                            {!editDraft.isAllDay && <label>End time<input type="time" value={editDraft.endTime} onChange={(event) => updateEditDraft('endTime', event.target.value)} /></label>}
                        </div>
                        <label className="library-rag-calendar-panel-check"><input type="checkbox" checked={editDraft.isAllDay} onChange={(event) => updateEditDraft('isAllDay', event.target.checked)} /> All day</label>
                        <label>Recipients<input value={editDraft.usersText} onChange={(event) => updateEditDraft('usersText', event.target.value)} placeholder="email@firm.com, picalendar@dalyblack.com" /></label>
                        <label>Reminders<input value={editDraft.remindersText} onChange={(event) => updateEditDraft('remindersText', event.target.value)} placeholder="30, 60, 90" /></label>
                        <label>Notes<textarea value={editDraft.notes} onChange={(event) => updateEditDraft('notes', event.target.value)} rows={3} /></label>
                        <div className="library-rag-calendar-panel-editor-actions"><button type="submit" className="action alt" disabled={savingEntry}>{savingEntry ? 'Saving...' : 'Save changes'}</button></div>
                    </form>
                )}
            </div>
        </>
    );
};
